import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import makeStyles from '@material-ui/core/styles/makeStyles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Button from '@material-ui/core/Button';
import TitanLogoImage from 'assets/images/authentication/titan-logo.png';
import SmallTitanLogoImage from 'assets/images/navigation/titan-logo-short.png';
import LogoutIcon from '@material-ui/icons/ExitToApp';
import HomeIcon from '@material-ui/icons/Home';
import ViewModuleIcon from '@material-ui/icons/ViewModule';

import UserSessionContext from 'context/UserSessionContext';

import { useLogout } from 'hooks/useAuth';
import SidebarPopper from '../SidebarPopper';

import SidebarMenu from '../SidebarMenu';
import { Search } from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
  root: {},
  item: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0
  },
  button: {
    color: theme.palette.white,
    padding: props => (props.isOpen ? '11px 26px' : '26px'),
    justifyContent: props => (props.isOpen ? 'flex-start' : 'center'),
    height: props => (props.isOpen ? '40px' : '70px'),
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightMedium,
    '&:hover': {
      backgroundColor: theme.palette.accent.main,
      borderRadius: 0
    }
  },
  icon: {
    color: theme.palette.icon,
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    marginRight: props => (props.isOpen ? '15px' : '0px')
  },
  media: {
    display: 'flex',
    maxWidth: '100%',
    padding: props => (props.isOpen ? '15px 14px' : '0px')
  },
  header: {
    display: 'flex',
    height: '70px'
  },
  homeLink: {
    textDecoration: 'none'
  }
}));

const SidebarNav = props => {
  const { pages, isOpen, handleMenuClick, menuState, isAdmin, ...rest } = props;

  const { session } = useContext(UserSessionContext);
  const { homepagePreference } = session;

  const [logout] = useLogout();
  const classes = useStyles({ isOpen });

  return (
    <List {...rest} className={classes.root}>
      <Link to={homepagePreference}>
        <ListItem className={classes.header} disableGutters key="Titan">
          {!isOpen && <img src={SmallTitanLogoImage} className={classes.media} alt="alt" />}
          {isOpen && <img src={TitanLogoImage} className={classes.media} alt="alt" />}
        </ListItem>
      </Link>
      <Link to={homepagePreference} className={classes.homeLink}>
        <ListItem className={classes.item} disableGutters key="Home">
          <Button className={classes.button}>
            <div className={classes.icon}>
              <HomeIcon />
            </div>
            {isOpen && 'Home'}
          </Button>
        </ListItem>
      </Link>
      <Link to="/" className={classes.homeLink}>
        <ListItem className={classes.item} disableGutters key="Heat Map">
          <Button className={classes.button}>
            <div className={classes.icon}>
              <ViewModuleIcon />
            </div>
            {isOpen && 'Heat Map'}
          </Button>
        </ListItem>
      </Link>
      <Link to="/reporting-generator" className={classes.homeLink}>
        <ListItem className={classes.item} disableGutters key="Report Generator">
          <Button className={classes.button}>
            <div className={classes.icon}>
              <Search />
            </div>
            {isOpen && 'Report Generator'}
          </Button>
        </ListItem>
      </Link>
      {pages.map(page =>
        !isAdmin && page.title === 'Admin' ? null : (
          <React.Fragment key={page.title}>
            <ListItem
              className={classes.item}
              disableGutters
              onClick={handleMenuClick}
              id={page.title}
            >
              <Button
                className={classes.button}
                style={{
                  backgroundColor: menuState === page.title && '#07A7D3',
                  borderRadius: menuState === page.title && 0
                }}
              >
                <div className={classes.icon}>{page.icon}</div>
                {isOpen && page.title}
              </Button>
            </ListItem>
            <SidebarPopper isOpen={menuState === page.title}>
              <SidebarMenu title={page.title} menuItems={page.data} />
            </SidebarPopper>
          </React.Fragment>
        )
      )}
      <ListItem className={classes.item} disableGutters key="Login">
        <Button onClick={logout} className={classes.button}>
          <div className={classes.icon}>
            <LogoutIcon />
          </div>
          {isOpen && 'Logout'}
        </Button>
      </ListItem>
    </List>
  );
};

SidebarNav.propTypes = {
  className: PropTypes.string,
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      href: PropTypes.string,
      icon: PropTypes.node
    })
  ).isRequired,
  isOpen: PropTypes.bool.isRequired,
  handleMenuClick: PropTypes.func.isRequired,
  menuState: PropTypes.string,
  isAdmin: PropTypes.bool.isRequired
};

SidebarNav.defaultProps = {
  className: '',
  menuState: null
};

export default SidebarNav;
